import { useEffect, useRef, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { APIProvider, Map, AdvancedMarker } from '@vis.gl/react-google-maps';
import {
  Building2, ArrowLeft, MapPin, Phone, Mail, Users,
  Plus, Trash2, Pencil, Loader2, RefreshCw, ChevronRight,
  Home, Briefcase, Star, Check, X,
  UserPlus, Shield, User, Settings, Crown, Wrench,
  FileText, SlidersHorizontal, Video, Radio, Activity, Navigation,
  Rocket, Heart, Link2, Search, Cpu, Repeat, CalendarDays, Unlink,
} from 'lucide-react';
import {
  fetchAccountById, updateAccount, fetchSitesByAccount,
  fetchInvoicesByAccount, addAccountMember, removeAccountMember, createSite,
  updateMemberCapabilities, updateMemberRole, fetchMemberSiteGrants, setMemberSiteGrants, syncClientSites,
  fetchAllClientSites, removeClientSite,
  updateAccountFeatures, setAccountDemo,
  fetchUnassignedSites, assignSiteToAccount, releaseSite,
  fetchPanelsAvailableToLink,
  fetchMemberCameraGrants, setMemberCameraGrants, fetchCamerasByAccount,
  type MemberCapabilities,
  type AccountFeatures,
  type ClientSiteEntry,
} from '@/services/accounts';
import { fetchDepartments, fetchMunicipalities } from '@/services/geo';
import { linkPanelToSite } from '@/services/alarms';
import { supabase } from '@/services/supabase';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { PermissionGate } from '@/components/PermissionGate';
import { PlaceSearchInput } from '@/components/PlaceSearchInput';
import { AlertRecipientsTab } from '@/components/accounts/AlertRecipientsTab';
import { AdminOnboardingQR } from '@/components/accounts/AdminOnboardingQR';
import { InvitesPage } from '@/pages/InvitesPage';
import { InvoiceFelActions, type AccountFelDefaults } from '@/components/billing/InvoiceFelActions';
import {
  listSubscriptions, createSubscription, updateSubscription, deleteSubscription,
  listBillingProducts,
  type BillingSubscription, type BillingProduct, type BillingInterval, type SubscriptionStatus,
} from '@/services/billing';
import { lookupNit, lookupCui } from '@/services/fel';
import { cn } from '@/lib/utils';
import type { AccountWithMembers, AccountMemberWithProfile, AlarmSiteWithContacts, Invoice, AccountType, AccountMemberRole, AccountTier, Profile, GeoDepartment, GeoMunicipality } from '@asi/shared';

const TIER_CONFIG: Record<AccountTier, { color: string; bg: string }> = {
  A: { color: 'text-amber-700 dark:text-amber-400', bg: 'bg-amber-500/15 border-amber-400/40' },
  B: { color: 'text-blue-700 dark:text-blue-400',   bg: 'bg-blue-500/15 border-blue-400/40' },
  C: { color: 'text-slate-600 dark:text-slate-400', bg: 'bg-slate-500/15 border-slate-400/40' },
};

function formatSeniority(createdAt: string): string {
  const months = (new Date().getFullYear() - new Date(createdAt).getFullYear()) * 12
    + (new Date().getMonth() - new Date(createdAt).getMonth());
  if (months < 1) return 'Nuevo cliente';
  if (months < 12) return `${months} mes${months !== 1 ? 'es' : ''} como cliente`;
  const y = Math.floor(months / 12), m = months % 12;
  return m > 0 ? `${y} año${y !== 1 ? 's' : ''} ${m} mes${m !== 1 ? 'es' : ''} como cliente` : `${y} año${y !== 1 ? 's' : ''} como cliente`;
}

const GOOGLE_MAPS_API_KEY = import.meta.env.VITE_GOOGLE_MAPS_API_KEY as string;
const GT_CENTER = { lat: 14.6349, lng: -90.5069 };

const TYPE_CONFIG: Record<AccountType, { label: string; icon: React.ReactNode; color: string }> = {
  residential: { label: 'Residencial', icon: <Home className="w-4 h-4" />, color: 'bg-blue-500/10 text-blue-600 dark:text-blue-400' },
  commercial:  { label: 'Comercio',    icon: <Briefcase className="w-4 h-4" />, color: 'bg-amber-500/10 text-amber-600 dark:text-amber-400' },
  enterprise:  { label: 'Empresa',     icon: <Star className="w-4 h-4" />,      color: 'bg-purple-500/10 text-purple-600 dark:text-purple-400' },
};

const MEMBER_ROLE_CONFIG: Record<AccountMemberRole, { label: string; icon: React.ReactNode; color: string }> = {
  owner:      { label: 'Propietario',   icon: <Crown className="w-3 h-3" />,   color: 'bg-amber-500/10 text-amber-600' },
  admin:      { label: 'Administrador', icon: <Crown className="w-3 h-3" />,   color: 'bg-orange-500/10 text-orange-600' },
  manager:    { label: 'Encargado',     icon: <Settings className="w-3 h-3" />, color: 'bg-purple-500/10 text-purple-600' },
  worker:     { label: 'Empleado',      icon: <User className="w-3 h-3" />,     color: 'bg-blue-500/10 text-blue-600' },
  technician: { label: 'Técnico',       icon: <Wrench className="w-3 h-3" />,   color: 'bg-green-500/10 text-green-600' },
  familiar:   { label: 'Familiar',      icon: <Heart className="w-3 h-3" />,    color: 'bg-pink-500/10 text-pink-600' },
};

const INVOICE_STATUS_CONFIG: Record<string, { label: string; color: string }> = {
  draft:   { label: 'Borrador', color: 'bg-gray-500/10 text-gray-500' },
  issued:  { label: 'Emitida',  color: 'bg-blue-500/10 text-blue-600' },
  paid:    { label: 'Pagada',   color: 'bg-green-500/10 text-green-600' },
  overdue: { label: 'Vencida',  color: 'bg-red-500/10 text-red-600' },
  void:    { label: 'Anulada',  color: 'bg-gray-500/10 text-gray-400' },
};

// DD/MM/YYYY display wrapper — Chrome ignores lang on date inputs, so we
// overlay a read-only text field and trigger showPicker() on the hidden input.
function DatePickerDDMMYYYY({ value, onChange }: { value: string; onChange: (v: string) => void }) {
  const nativeRef = useRef<HTMLInputElement>(null);
  const display = value?.length === 10
    ? `${value.slice(8, 10)}/${value.slice(5, 7)}/${value.slice(0, 4)}`
    : '';
  function open() {
    const el = nativeRef.current;
    if (!el) return;
    try { el.showPicker(); } catch { el.focus(); }
  }
  return (
    <div className="relative mt-1">
      <Input type="text" value={display} readOnly placeholder="DD/MM/YYYY"
        className="cursor-pointer pr-9" onClick={open} />
      <CalendarDays className="absolute right-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground pointer-events-none" />
      <input ref={nativeRef} type="date" value={value}
        onChange={(e) => onChange(e.target.value)}
        className="sr-only" tabIndex={-1} />
    </div>
  );
}

export function AccountDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [account, setAccount] = useState<AccountWithMembers | null>(null);
  const [sites, setSites] = useState<AlarmSiteWithContacts[]>([]);
  const [qrSiteId, setQrSiteId] = useState<string>('');
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('sites');
  const [editing, setEditing] = useState(false);
  const [editForm, setEditForm] = useState<Partial<AccountWithMembers>>({});
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [departments, setDepartments] = useState<GeoDepartment[]>([]);
  const [municipalities, setMunicipalities] = useState<GeoMunicipality[]>([]);

  // Add member state
  const [showAddMember, setShowAddMember] = useState(false);
  const [allUsers, setAllUsers] = useState<Profile[]>([]);
  const [memberUserId, setMemberUserId] = useState('');
  const [memberSearch, setMemberSearch] = useState('');
  const [memberRole, setMemberRole] = useState<AccountMemberRole>('worker');
  // Pool A (managed/site access) and Pool B (app-alert license) are
  // independent — a member can have either, both, or neither is blocked at
  // submit. Self-service accounts have no Pool A, so that checkbox is hidden
  // and only the license one applies.
  const [memberHasManagedAccess, setMemberHasManagedAccess] = useState(true);
  const [memberHasAppLicense, setMemberHasAppLicense] = useState(true);
  const [addingMember, setAddingMember] = useState(false);
  const [memberError, setMemberError] = useState<string | null>(null);

  // Create site state
  const [showCreateSite, setShowCreateSite] = useState(false);
  const [siteForm, setSiteForm] = useState({ name: '', address: '', tipo: '', clave: '', notes: '', department_id: 0, municipality_id: 0, lat: null as number | null, lng: null as number | null, camera_mode: 'snapshot' as 'snapshot' | 'livestream' });
  const [siteMunicipalities, setSiteMunicipalities] = useState<GeoMunicipality[]>([]);
  const [creatingSite, setCreatingSite] = useState(false);
  const [siteError, setSiteError] = useState<string | null>(null);

  // Link existing (HPP) site state
  const [showLinkSite, setShowLinkSite] = useState(false);
  const [unassignedSites, setUnassignedSites] = useState<import('@asi/shared').AlarmSite[]>([]);
  const [loadingUnassigned, setLoadingUnassigned] = useState(false);
  const [linkSiteId, setLinkSiteId] = useState('');
  const [linkingState, setLinkingState] = useState(false);
  const [linkError, setLinkError] = useState<string | null>(null);
  const [linkSearch, setLinkSearch] = useState('');

  // Add panel to existing site state
  const [showAddPanel, setShowAddPanel] = useState(false);
  const [availablePanels, setAvailablePanels] = useState<{ id: string; name: string; serial_number: string; model: string | null; site_name: string | null }[]>([]);
  const [loadingPanels, setLoadingPanels] = useState(false);
  const [addPanelSearch, setAddPanelSearch] = useState('');
  const [addPanelId, setAddPanelId] = useState('');
  const [addPanelSiteId, setAddPanelSiteId] = useState('');
  const [addingPanel, setAddingPanel] = useState(false);
  const [addPanelError, setAddPanelError] = useState<string | null>(null);

  // Delegation modal state
  const DEFAULT_CAPS: MemberCapabilities = {
    can_view_cameras: true,
    can_view_panels: true,
    can_arm_disarm: false,
    can_view_history: true,
  };
  const [delegateModal, setDelegateModal] = useState<AccountMemberWithProfile | null>(null);
  const [delegateCaps, setDelegateCaps] = useState<MemberCapabilities>(DEFAULT_CAPS);
  const [delegateRole, setDelegateRole] = useState<AccountMemberRole>('worker');

  // NIT lookup state (Consulta NIT against INFILE padrón)
  const [nitLookup, setNitLookup] = useState<{ loading: boolean; result: string | null; error: string | null }>({
    loading: false, result: null, error: null,
  });
  const handleLookupNit = async () => {
    const raw = (editForm.nit ?? '').trim();
    if (!raw) {
      setNitLookup({ loading: false, result: null, error: 'Ingresa el NIT o CUI primero' });
      return;
    }
    setNitLookup({ loading: true, result: null, error: null });
    // Heuristic: 13 numeric digits → CUI (DPI). Anything else → NIT.
    const digitsOnly = raw.replace(/[-\s]/g, '');
    const isCui = /^\d{13}$/.test(digitsOnly);
    try {
      if (isCui) {
        const res = await lookupCui(digitsOnly);
        if (res.valid && res.name) {
          setEditForm((f) => ({
            ...f,
            billing_name: f.billing_name?.trim() ? f.billing_name : res.name,
          }));
          const tag = res.deceased ? ' (FALLECIDO)' : '';
          setNitLookup({ loading: false, result: `\u2713 CUI: ${res.name}${tag} (${res.source})`, error: null });
        } else {
          setNitLookup({ loading: false, result: null, error: `CUI no v\u00e1lido (${res.status})` });
        }
      } else {
        const res = await lookupNit(raw, account?.id);
        if (res.valid && res.name) {
          setEditForm((f) => ({
            ...f,
            billing_name: f.billing_name?.trim() ? f.billing_name : res.name,
          }));
          setNitLookup({ loading: false, result: `\u2713 NIT: ${res.name} (${res.source})`, error: null });
        } else if (res.status === 'CREDENTIALS_NOT_REGISTERED') {
          setNitLookup({ loading: false, result: null, error: 'Consulta SAT no disponible en este ambiente.' });
        } else {
          const d = res.debug;
          const dbg = d
            ? ` · [${d.fel_env}] HTTP ${d.http_status} · enviado: emisor_codigo="${d.sent_emisor_codigo}", clave="${d.sent_clave_first4}…" (${d.sent_clave_len}c), nit="${d.sent_nit_consulta}" · respuesta: ${d.raw_body}`
            : '';
          setNitLookup({
            loading: false,
            result:  null,
            error:   (res.message || `NIT no v\u00e1lido (${res.status})`) + dbg,
          });
        }
      }
    } catch (e) {
      setNitLookup({ loading: false, result: null, error: e instanceof Error ? e.message : 'Error al consultar' });
    }
  };
  const [delegateSiteIds, setDelegateSiteIds] = useState<string[]>([]);
  const [delegateCameraIds, setDelegateCameraIds] = useState<string[]>([]);
  const [cameras, setCameras] = useState<{ id: string; name: string }[]>([]);
  const [loadingGrants, setLoadingGrants] = useState(false);
  const [savingGrants, setSavingGrants] = useState(false);
  const [allClientSites, setAllClientSites] = useState<ClientSiteEntry[]>([]);
  const [removingSiteId, setRemovingSiteId] = useState<string | null>(null);

  // Feature flags state
  const [savingFeatures, setSavingFeatures] = useState(false);

  // Patrol radius state
  const [patrolRadiusInput, setPatrolRadiusInput] = useState<number>(15);
  const [savingRadius, setSavingRadius] = useState(false);

  // Billing subscriptions state
  const [subscriptions, setSubscriptions] = useState<BillingSubscription[]>([]);
  const [billingProducts, setBillingProducts] = useState<BillingProduct[]>([]);
  const [showSubModal, setShowSubModal] = useState(false);
  const [editingSub, setEditingSub] = useState<BillingSubscription | null>(null);
  const [subForm, setSubForm] = useState<Partial<BillingSubscription>>({});
  const [savingSub, setSavingSub] = useState(false);
  const [subError, setSubError] = useState<string | null>(null);

  // Member capacity pools (site access + app licenses)
  const [capacity, setCapacity] = useState<{
    is_managed: boolean;
    site_pool: { used: number; cap: number } | null;
    license_pool: { used: number; cap: number | null };
  } | null>(null);

  useEffect(() => {
    if (!account?.id) return;
    supabase
      .rpc('account_member_capacity', { p_account_id: account.id })
      .then(({ data, error }) => {
        if (!error && data) setCapacity(data as typeof capacity);
      });
  }, [account?.id]);

  async function load() {
    if (!id) return;
    setLoading(true);
    setAccount(null);
    setError(null);
    try {
      const acc = await fetchAccountById(id);
      setAccount(acc);
      setEditForm(acc);
      setPatrolRadiusInput(acc.patrol_radius_km ?? 15);
      // Serialize geo + data requests to avoid auth-lock contention (see docs/SUPABASE_AUTH_LOCK_DEADLOCK.md)
      const depts = await fetchDepartments().catch((): GeoDepartment[] => []);
      setDepartments(depts);
      // Sites, invoices and subscriptions are non-critical — load in parallel after geo
      const [s, inv, subs, prods] = await Promise.allSettled([
        fetchSitesByAccount(id),
        fetchInvoicesByAccount(id),
        listSubscriptions({ accountId: id }),
        listBillingProducts(),
      ]);
      if (s.status === 'fulfilled') setSites(s.value);
      if (inv.status === 'fulfilled') setInvoices(inv.value);
      if (subs.status === 'fulfilled') setSubscriptions(subs.value);
      if (prods.status === 'fulfilled') setBillingProducts(prods.value);
    } catch (err) {
      // Extract message from Supabase PostgrestError or regular Error
      const msg = (err as { message?: string })?.message ?? String(err);
      setError(msg);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { load(); }, [id]);

  // Realtime: refresh members when someone joins via invite code
  useEffect(() => {
    if (!id) return;
    const channel = supabase
      .channel(`account_members:${id}`)
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'account_members', filter: `account_id=eq.${id}` },
        () => { void load(); },
      )
      .subscribe();
    return () => { void supabase.removeChannel(channel); };
  }, [id]);

  useEffect(() => {
    const deptId = editForm.department_id;
    if (deptId) {
      fetchMunicipalities(deptId).then(setMunicipalities).catch(() => {});
    } else {
      setMunicipalities([]);
    }
  }, [editForm.department_id]);

  useEffect(() => {
    if (siteForm.department_id) {
      fetchMunicipalities(siteForm.department_id).then(setSiteMunicipalities).catch(() => {});
    } else {
      setSiteMunicipalities([]);
    }
  }, [siteForm.department_id]);

  async function loadUsers() {
    const { data } = await supabase.from('profiles').select('id, full_name, email, role').order('full_name');
    if (data) setAllUsers(data as Profile[]);
  }

  function startEdit() {
    setEditForm(account ?? {});
    setEditing(true);
    setActiveTab('info');
  }

  async function handleSave() {
    if (!id || !editForm) return;
    setSaving(true);
    setError(null);
    try {
      await updateAccount(id, {
        name: editForm.name,
        type: editForm.type,
        nit: editForm.nit ?? undefined,
        phone: editForm.phone ?? undefined,
        billing_email: editForm.billing_email ?? undefined,
        billing_name: editForm.billing_name ?? undefined,
        billing_address: editForm.billing_address ?? undefined,
        address: editForm.address ?? undefined,
        department_id: editForm.department_id ?? undefined,
        municipality_id: editForm.municipality_id ?? undefined,
        monthly_fee: editForm.monthly_fee ?? undefined,
        billing_day: editForm.billing_day,
        notes: editForm.notes ?? undefined,
        is_demo: editForm.is_demo ?? false,
        client_tier: (editForm as any).client_tier ?? null,
      });
      setEditing(false);
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error al guardar');
    } finally {
      setSaving(false);
    }
  }

  async function handleAddMember() {
    if (!id || !memberUserId) return;
    if (!memberHasManagedAccess && !memberHasAppLicense) {
      setMemberError('Selecciona al menos un tipo de acceso: cuenta gestionada, alertas por app, o ambos.');
      return;
    }
    setAddingMember(true);
    setMemberError(null);
    try {
      await addAccountMember(id, memberUserId, memberRole, memberHasAppLicense, memberHasManagedAccess);
      // New members get access to ALL account sites by default (empty grants = all)
      // — but only when managed access was actually granted; an app-license-only
      // member shouldn't see cameras/panels at all.
      if (memberHasManagedAccess) {
        const accountSiteIds = sites.map((s) => s.id);
        await syncClientSites(memberUserId, accountSiteIds, []);
      }
      setShowAddMember(false);
      setMemberUserId('');
      setMemberSearch('');
      setMemberError(null);
      const { data: capData, error: capErr } = await supabase
        .rpc('account_member_capacity', { p_account_id: id });
      if (!capErr && capData) setCapacity(capData as typeof capacity);
      await load();
    } catch (err) {
      // Supabase/PostgREST errors are plain objects ({message, details, hint,
      // code}), NOT instanceof Error — the old check silently swallowed the
      // actual (Spanish, descriptive) exception text from our DB triggers and
      // always showed the generic fallback instead.
      const msg = (err && typeof err === 'object' && 'message' in err && typeof (err as { message?: unknown }).message === 'string')
        ? (err as { message: string }).message
        : err instanceof Error ? err.message : 'Error al agregar miembro';
      setMemberError(msg);
    } finally {
      setAddingMember(false);
    }
  }

  async function handleCreateSite() {
    if (!id || !siteForm.name.trim()) return;
    setCreatingSite(true);
    setSiteError(null);
    try {
      await createSite(id, {
        name: siteForm.name.trim(),
        address: siteForm.address || undefined,
        tipo: siteForm.tipo || undefined,
        clave: siteForm.clave || undefined,
        notes: siteForm.notes || undefined,
        department_id: siteForm.department_id || undefined,
        municipality_id: siteForm.municipality_id || undefined,
        lat: siteForm.lat ?? undefined,
        lng: siteForm.lng ?? undefined,
        camera_mode: siteForm.camera_mode,
      });
      setShowCreateSite(false);
      setSiteForm({ name: '', address: '', tipo: '', clave: '', notes: '', department_id: 0, municipality_id: 0, lat: null, lng: null, camera_mode: 'snapshot' });
      setSiteMunicipalities([]);
      // Reload only sites
      const s = await fetchSitesByAccount(id);
      setSites(s);
    } catch (err) {
      setSiteError((err as { message?: string })?.message ?? String(err));
    } finally {
      setCreatingSite(false);
    }
  }

  async function handleOpenLinkSite() {
    setLinkError(null);
    setLinkSiteId('');
    setShowLinkSite(true);
    setLoadingUnassigned(true);
    try {
      const list = await fetchUnassignedSites();
      setUnassignedSites(list);
    } catch {
      setUnassignedSites([]);
    } finally {
      setLoadingUnassigned(false);
    }
  }

  async function handleLinkSite() {
    if (!id || !linkSiteId) return;
    setLinkingState(true);
    setLinkError(null);
    try {
      await assignSiteToAccount(linkSiteId, id);
      setShowLinkSite(false);
      setLinkSiteId('');
      const s = await fetchSitesByAccount(id);
      setSites(s);
    } catch (err) {
      setLinkError((err as { message?: string })?.message ?? String(err));
    } finally {
      setLinkingState(false);
    }
  }

  const [releasingSiteId, setReleasingSiteId] = useState<string | null>(null);
  async function handleReleaseSite(siteId: string, siteName: string) {
    if (!id) return;
    if (!confirm(`¿Liberar "${siteName}" de esta cuenta? El panel quedará sin dueño y disponible para que otro cliente lo reclame con un nuevo código QR.`)) return;
    setReleasingSiteId(siteId);
    try {
      await releaseSite(siteId);
      const s = await fetchSitesByAccount(id);
      setSites(s);
      // If this was the account's last site and it had been auto-promoted to
      // "managed" (e.g. by a QR attach), it's now an empty shell that would
      // otherwise keep showing the Devices tab in the client's app forever.
      const regSrc = (account as unknown as Record<string, unknown> | null)?.registration_source as string | undefined;
      if (s.length === 0 && regSrc === 'asi_managed'
        && confirm('Esta cuenta se quedó sin sitios. ¿También revertirla a modo "solo app" (quita la tab Dispositivos del cliente)?')) {
        await updateAccount(id, { registration_source: 'self_service' });
        setAccount((prev) => prev ? { ...prev, registration_source: 'self_service' } as typeof prev : prev);
      }
    } catch (err) {
      alert((err as { message?: string })?.message ?? String(err));
    } finally {
      setReleasingSiteId(null);
    }
  }

  async function handleOpenAddPanel() {
    setAddPanelError(null);
    setAddPanelId('');
    setAddPanelSiteId('');
    setAddPanelSearch('');
    setShowAddPanel(true);
    setLoadingPanels(true);
    try {
      const panels = await fetchPanelsAvailableToLink();
      setAvailablePanels(panels);
    } catch {
      setAvailablePanels([]);
    } finally {
      setLoadingPanels(false);
    }
  }

  async function handleAddPanel() {
    if (!addPanelId || !addPanelSiteId) return;
    setAddingPanel(true);
    setAddPanelError(null);
    try {
      await linkPanelToSite(addPanelId, addPanelSiteId);
      setShowAddPanel(false);
      setAddPanelId('');
      setAddPanelSiteId('');
      const s = await fetchSitesByAccount(id!);
      setSites(s);
    } catch (err) {
      setAddPanelError((err as { message?: string })?.message ?? String(err));
    } finally {
      setAddingPanel(false);
    }
  }

  async function handleRemoveMember(memberId: string) {
    await removeAccountMember(memberId);
    await load();
  }

  async function handleOpenDelegate(member: AccountMemberWithProfile) {
    setDelegateModal(member);
    setDelegateRole(member.role as AccountMemberRole);
    setAllClientSites([]);
    setDelegateCaps({
      can_view_cameras: member.can_view_cameras,
      can_view_panels:  member.can_view_panels,
      can_arm_disarm:   member.can_arm_disarm,
      can_view_history: member.can_view_history,
    });
    setLoadingGrants(true);
    try {
      const [grants, cameraGrants, allSites, accountCameras] = await Promise.all([
        fetchMemberSiteGrants(member.id),
        fetchMemberCameraGrants(member.id),
        fetchAllClientSites(member.user_id),
        fetchCamerasByAccount(id!),
      ]);
      setDelegateSiteIds(grants.map((g) => g.site_id));
      setDelegateCameraIds(cameraGrants.map((g) => g.camera_id));
      setAllClientSites(allSites);
      setCameras(accountCameras);
    } finally {
      setLoadingGrants(false);
    }
  }

  async function handleRemoveClientSite(userId: string, siteId: string) {
    setRemovingSiteId(siteId);
    try {
      await removeClientSite(userId, siteId);
      setAllClientSites((prev) => prev.filter((s) => s.site_id !== siteId));
      // Also remove from delegateSiteIds if it was there
      setDelegateSiteIds((prev) => prev.filter((id) => id !== siteId));
    } finally {
      setRemovingSiteId(null);
    }
  }

  async function handleSaveDelegate() {
    if (!delegateModal) return;
    setSavingGrants(true);
    try {
      if (delegateRole !== delegateModal.role) {
        await updateMemberRole(delegateModal.id, delegateRole);
      }
      await updateMemberCapabilities(delegateModal.id, delegateCaps);
      await setMemberSiteGrants(delegateModal.id, delegateSiteIds);
      await setMemberCameraGrants(delegateModal.id, delegateCameraIds);
      // Sync client_sites so mobile app and RLS policies reflect the same access.
      const accountSiteIds = sites.map((s) => s.id);
      await syncClientSites(delegateModal.user_id, accountSiteIds, delegateSiteIds);
      setDelegateModal(null);
      await load();
    } finally {
      setSavingGrants(false);
    }
  }

  // ── Subscription handlers ─────────────────────────────────────────────────
  function toLocalDateStr(d?: string | null): string {
    const base = d ? new Date(d) : new Date();
    return `${base.getFullYear()}-${String(base.getMonth() + 1).padStart(2, '0')}-${String(base.getDate()).padStart(2, '0')}`;
  }
  function openCreateSub() {
    const today = toLocalDateStr();
    setEditingSub(null);
    setSubForm({
      account_id: id!,
      site_id: null,
      product_id: '',
      quantity: 1,
      unit_price_override: null,
      description_override: null,
      billing_interval: 'monthly',
      billing_day: account?.billing_day ?? 1,
      start_date: today,
      end_date: null,
      next_billing_date: today,
      auto_emit_fel: false,
      auto_charge: false,
      status: 'active',
      notes: null,
    });
    setSubError(null);
    setShowSubModal(true);
  }

  function openEditSub(sub: BillingSubscription) {
    setEditingSub(sub);
    setSubForm({
      ...sub,
      start_date: sub.start_date?.slice(0, 10) ?? toLocalDateStr(),
      end_date: sub.end_date?.slice(0, 10) ?? null,
      next_billing_date: sub.next_billing_date?.slice(0, 10) ?? toLocalDateStr(),
    });
    setSubError(null);
    setShowSubModal(true);
  }

  function openBuyCapacityAddon() {
    const prod = billingProducts.find((p) => p.code === 'SITE_MEMBER_ADDON');
    const today = toLocalDateStr();
    setEditingSub(null);
    setSubForm({
      account_id: id!,
      site_id: null,
      product_id: prod?.id ?? '',
      quantity: 1,
      unit_price_override: prod?.default_unit_price ?? null,
      description_override: null,
      billing_interval: 'monthly',
      billing_day: account?.billing_day ?? 1,
      start_date: today,
      end_date: null,
      next_billing_date: today,
      auto_emit_fel: false,
      auto_charge: false,
      status: 'active',
      notes: null,
    });
    setSubError(null);
    setShowSubModal(true);
  }

  async function handleSaveSub() {
    if (!subForm.product_id || !subForm.billing_interval || !subForm.next_billing_date) return;
    setSavingSub(true);
    setSubError(null);
    try {
      if (editingSub) {
        await updateSubscription(editingSub.id, subForm as Parameters<typeof updateSubscription>[1]);
      } else {
        await createSubscription({
          account_id:           id!,
          site_id:              subForm.site_id ?? null,
          product_id:           subForm.product_id!,
          quantity:             subForm.quantity ?? 1,
          unit_price_override:  subForm.unit_price_override ?? null,
          description_override: subForm.description_override ?? null,
          billing_interval:     subForm.billing_interval!,
          billing_day:          subForm.billing_day ?? 1,
          start_date:           subForm.start_date ?? toLocalDateStr(),
          end_date:             subForm.end_date ?? null,
          next_billing_date:    subForm.next_billing_date!,
          auto_emit_fel:        subForm.auto_emit_fel ?? false,
          auto_charge:          subForm.auto_charge ?? false,
          status:               subForm.status ?? 'active',
          notes:                subForm.notes ?? null,
        });
      }
      setShowSubModal(false);
      const updated = await listSubscriptions({ accountId: id! });
      setSubscriptions(updated);
      const { data: capData, error: capErr } = await supabase
        .rpc('account_member_capacity', { p_account_id: id! });
      if (!capErr && capData) setCapacity(capData as typeof capacity);
    } catch (err) {
      setSubError(err instanceof Error ? err.message : 'Error al guardar');
    } finally {
      setSavingSub(false);
    }
  }

  async function handleDeleteSub(subId: string) {
    if (!confirm('¿Eliminar esta suscripción? No afecta las facturas ya generadas.')) return;
    await deleteSubscription(subId);
    setSubscriptions((prev) => prev.filter((s) => s.id !== subId));
  }

  async function handleToggleFeature(flag: keyof AccountFeatures) {
    if (!account) return;
    setSavingFeatures(true);
    try {
      const current = (account as any)[flag] ?? true;
      const update = { [flag]: !current } as Partial<AccountFeatures>;
      await updateAccountFeatures(account.id, update);
      await load();
    } finally {
      setSavingFeatures(false);
    }
  }

  async function handleSavePatrolRadius() {
    if (!account) return;
    const km = Math.max(1, Math.min(100, Math.round(patrolRadiusInput)));
    setSavingRadius(true);
    try {
      await updateAccount(account.id, { patrol_radius_km: km });
      await load();
    } finally {
      setSavingRadius(false);
    }
  }

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-[300px]">
        <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!account) {
    return (
      <div className="p-6 space-y-3">
        <Button variant="ghost" size="sm" onClick={() => navigate('/accounts')}>
          <ArrowLeft className="w-4 h-4 mr-1" /> Volver
        </Button>
        <p className="text-muted-foreground text-sm">{error ?? 'Cuenta no encontrada.'}</p>
        {error && (
          <Button size="sm" variant="outline" onClick={load}>
            <RefreshCw className="w-4 h-4 mr-1" /> Reintentar
          </Button>
        )}
      </div>
    );
  }

  const typeCfg = TYPE_CONFIG[account.type];

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="sm" onClick={() => navigate('/accounts')}>
          <ArrowLeft className="w-4 h-4" />
        </Button>
        <div className={cn('p-2 rounded-lg', typeCfg.color)}>{typeCfg.icon}</div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <h1 className="text-xl font-bold text-foreground truncate">{account.name}</h1>
            <Badge variant="outline" className="font-mono text-xs">{account.account_number}</Badge>
            <Badge className={cn('text-xs', typeCfg.color)}>{typeCfg.label}</Badge>
            {account.client_tier && (
              <span className={cn(
                'inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-bold border',
                TIER_CONFIG[account.client_tier].bg,
                TIER_CONFIG[account.client_tier].color,
              )}>
                <Star className="w-3 h-3" />
                Tier {account.client_tier}
              </span>
            )}
            {account.is_demo && (
              <Badge className="text-xs bg-amber-500 hover:bg-amber-500 text-white gap-1">
                <Rocket className="w-3 h-3" />
                DEMO
              </Badge>
            )}
          </div>
          <div className="flex items-center gap-3 text-xs text-muted-foreground mt-1">
            {account.city && <span className="flex items-center gap-1"><MapPin className="w-3 h-3" />{account.city}</span>}
            {account.phone && <span className="flex items-center gap-1"><Phone className="w-3 h-3" />{account.phone}</span>}
            {account.billing_email && <span className="flex items-center gap-1"><Mail className="w-3 h-3" />{account.billing_email}</span>}
            <span className="flex items-center gap-1"><CalendarDays className="w-3 h-3" />{formatSeniority(account.created_at)}</span>
          </div>
        </div>
        <PermissionGate require="accounts.manage">
          <Button variant="outline" size="sm" onClick={startEdit}>
            <Pencil className="w-4 h-4 mr-1" />
            Editar
          </Button>
        </PermissionGate>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="sites">
            Sitios <Badge variant="secondary" className="ml-1">{sites.length}</Badge>
          </TabsTrigger>
          <TabsTrigger value="members">
            Miembros <Badge variant="secondary" className="ml-1">{account.members?.length ?? 0}</Badge>
          </TabsTrigger>
          <TabsTrigger value="invites">Invitaciones</TabsTrigger>
          <TabsTrigger value="recipients">Receptores</TabsTrigger>
          <TabsTrigger value="billing">Facturación</TabsTrigger>
          <TabsTrigger value="services">Servicios</TabsTrigger>
          <TabsTrigger value="info">Datos</TabsTrigger>
        </TabsList>

        {/* ── Tab: Sitios ── */}
        <TabsContent value="sites" className="mt-4 space-y-3">
          <div className="flex justify-end gap-2 flex-wrap">
            <PermissionGate require="accounts.manage">
              <Button size="sm" variant="outline" onClick={handleOpenAddPanel} disabled={sites.length === 0}>
                <Cpu className="w-4 h-4 mr-1" />
                Agregar panel HPP
              </Button>
              <Button size="sm" variant="outline" onClick={handleOpenLinkSite}>
                <Link2 className="w-4 h-4 mr-1" />
                Vincular sitio HPP
              </Button>
              <Button size="sm" onClick={() => { setSiteError(null); setShowCreateSite(true); }}>
                <Plus className="w-4 h-4 mr-1" />
                Nuevo sitio
              </Button>
            </PermissionGate>
          </div>

          {sites.length === 0 ? (
            <Card>
              <CardContent className="py-10 text-center">
                <MapPin className="w-10 h-10 text-muted-foreground mx-auto mb-2 opacity-30" />
                <p className="text-muted-foreground text-sm">Esta cuenta no tiene sitios registrados.</p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-2">
              {sites.map((site) => (
                <Link
                  key={site.id}
                  to={`/sites/${site.id}`}
                  className="flex items-center gap-4 p-4 rounded-xl border border-border bg-card hover:bg-muted/40 transition-colors"
                >
                  <MapPin className="w-5 h-5 text-muted-foreground shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-foreground truncate">{site.name}</p>
                    <p className="text-xs text-muted-foreground">
                      {[site.address, site.city].filter(Boolean).join(', ')}
                      {site.tipo && <span className="ml-2 text-muted-foreground/70">· {site.tipo}</span>}
                    </p>
                    {site.contacts.length > 0 && (
                      <p className="text-xs text-muted-foreground mt-0.5">
                        {site.contacts.length} contacto{site.contacts.length !== 1 ? 's' : ''}
                      </p>
                    )}
                  </div>
                  <div className="flex items-center gap-2 shrink-0 text-xs">
                    {site.is_active
                      ? <span className="text-green-500 font-medium">Activo</span>
                      : <span className="text-muted-foreground">Inactivo</span>}
                    <PermissionGate require="accounts.manage">
                      <Button
                        size="sm"
                        variant="ghost"
                        className="h-7 px-2 text-muted-foreground hover:text-destructive"
                        disabled={releasingSiteId === site.id}
                        onClick={(e) => { e.preventDefault(); e.stopPropagation(); void handleReleaseSite(site.id, site.name); }}
                        title="Liberar sitio (dejarlo disponible para otro cliente)"
                      >
                        {releasingSiteId === site.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Unlink className="w-4 h-4" />}
                      </Button>
                    </PermissionGate>
                    <ChevronRight className="w-4 h-4 text-muted-foreground" />
                  </div>
                </Link>
              ))}
            </div>
          )}

          {/* Create site modal */}
          {showCreateSite && (
            <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
              <div className="bg-background rounded-xl shadow-2xl w-full max-w-md p-6 space-y-4">
                <h3 className="font-semibold text-foreground">Nuevo sitio</h3>
                <div className="space-y-3">
                  <div>
                    <label className="text-sm font-medium text-foreground">Nombre *</label>
                    <Input
                      placeholder="Ej. Casa principal, Bodega zona 12"
                      value={siteForm.name}
                      onChange={(e) => setSiteForm((f) => ({ ...f, name: e.target.value }))}
                      className="mt-1"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="text-sm font-medium text-foreground">Tipo</label>
                      <Input
                        placeholder="Residencial, Comercio…"
                        value={siteForm.tipo}
                        onChange={(e) => setSiteForm((f) => ({ ...f, tipo: e.target.value }))}
                        className="mt-1"
                      />
                    </div>
                    <div>
                      <label className="text-sm font-medium text-foreground">Palabra Clave</label>
                      <Input
                        placeholder="Palabra de seguridad"
                        value={siteForm.clave}
                        onChange={(e) => setSiteForm((f) => ({ ...f, clave: e.target.value }))}
                        className="mt-1"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-foreground">Dirección</label>
                    <Input
                      placeholder="5a Av 12-34, Zona 10"
                      value={siteForm.address}
                      onChange={(e) => setSiteForm((f) => ({ ...f, address: e.target.value }))}
                      className="mt-1"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="text-sm font-medium text-foreground">Departamento</label>
                      <select
                        value={siteForm.department_id || ''}
                        onChange={(e) => setSiteForm((f) => ({ ...f, department_id: Number(e.target.value) || 0, municipality_id: 0 }))}
                        className="mt-1 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
                      >
                        <option value="">— Seleccione —</option>
                        {departments.map((d) => (
                          <option key={d.id} value={d.id}>{d.name}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-foreground">Municipio</label>
                      <select
                        value={siteForm.municipality_id || ''}
                        onChange={(e) => setSiteForm((f) => ({ ...f, municipality_id: Number(e.target.value) || 0 }))}
                        disabled={!siteForm.department_id}
                        className="mt-1 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:opacity-50"
                      >
                        <option value="">— Seleccione —</option>
                        {siteMunicipalities.map((m) => (
                          <option key={m.id} value={m.id}>{m.name}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-foreground">Notas</label>
                    <textarea
                      className="mt-1 w-full rounded-md border border-border bg-background px-3 py-2 text-sm text-foreground min-h-[60px] resize-none"
                      placeholder="Indicaciones especiales, piso, local, etc."
                      value={siteForm.notes}
                      onChange={(e) => setSiteForm((f) => ({ ...f, notes: e.target.value }))}
                    />
                  </div>
                  {/* Map pin picker */}
                  <div>
                    <label className="text-sm font-medium text-foreground flex items-center gap-1.5 mb-1">
                      <MapPin className="w-3.5 h-3.5 text-red-500" />
                      Pin de emergencia
                    </label>
                    <p className="text-xs text-muted-foreground mb-2">
                      Haz clic en el mapa para fijar el pin de emergencia del sitio.
                    </p>
                    {siteForm.lat && siteForm.lng && (
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-xs font-mono text-muted-foreground">
                          {siteForm.lat.toFixed(6)}, {siteForm.lng.toFixed(6)}
                        </span>
                        <button
                          type="button"
                          onClick={() => setSiteForm((f) => ({ ...f, lat: null, lng: null }))}
                          className="text-xs text-destructive hover:underline"
                        >
                          Quitar pin
                        </button>
                      </div>
                    )}
                    <APIProvider apiKey={GOOGLE_MAPS_API_KEY}>
                      <PlaceSearchInput
                        mapId="site-create"
                        onPlaceSelect={(lat, lng) => setSiteForm((f) => ({ ...f, lat, lng }))}
                      />
                      <div className="rounded-lg overflow-hidden border border-border" style={{ height: 220 }}>
                        <Map
                          defaultCenter={siteForm.lat && siteForm.lng
                            ? { lat: siteForm.lat, lng: siteForm.lng }
                            : GT_CENTER}
                          defaultZoom={siteForm.lat ? 16 : 10}
                          gestureHandling="greedy"
                          disableDefaultUI={false}
                          mapId="site-create"
                          style={{ width: '100%', height: '100%' }}
                          onClick={(e: { detail: { latLng?: { lat: number; lng: number } | null } }) => {
                            if (e.detail.latLng) {
                              setSiteForm((f) => ({
                                ...f,
                                lat: e.detail.latLng!.lat,
                                lng: e.detail.latLng!.lng,
                              }));
                            }
                          }}
                        >
                          {siteForm.lat && siteForm.lng && (
                            <AdvancedMarker position={{ lat: siteForm.lat, lng: siteForm.lng }} />
                          )}
                        </Map>
                      </div>
                    </APIProvider>
                  </div>
                </div>
                <div className="border-t pt-4">
                  <label className="text-sm font-medium block mb-1">Modo de vista de cámaras en miniaturas</label>
                  <div className="flex items-center gap-3 mt-1">
                    <button type="button"
                      onClick={() => setSiteForm((f) => ({ ...f, camera_mode: f.camera_mode === 'livestream' ? 'snapshot' : 'livestream' }))}
                      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                        siteForm.camera_mode === 'livestream' ? 'bg-primary' : 'bg-input'
                      }`}>
                      <span className={`inline-block h-4 w-4 transform rounded-full bg-white shadow-sm transition-transform ${
                        siteForm.camera_mode === 'livestream' ? 'translate-x-6' : 'translate-x-1'
                      }`} />
                    </button>
                    <span className="text-sm font-medium">
                      {siteForm.camera_mode === 'livestream' ? '🟢 Video en vivo' : '📷 Snapshots cada 5s'}
                    </span>
                  </div>
                  <p className="text-xs text-muted-foreground mt-2">
                    {siteForm.camera_mode === 'livestream'
                      ? 'Las miniaturas mostrarán video en tiempo real. Usa más datos. La vista completa siempre es en vivo.'
                      : 'Las miniaturas se actualizarán con una foto cada 5 segundos. Menos datos. La vista completa siempre es en vivo.'}
                  </p>
                </div>
                {siteError && <p className="text-sm text-red-500 bg-red-500/10 rounded-lg px-3 py-2">{siteError}</p>}
                <div className="flex gap-2">
                  <Button variant="outline" className="flex-1" onClick={() => setShowCreateSite(false)}>Cancelar</Button>
                  <Button
                    className="flex-1"
                    disabled={!siteForm.name.trim() || creatingSite}
                    onClick={handleCreateSite}
                  >
                    {creatingSite ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Plus className="w-4 h-4 mr-1" />}
                    Crear sitio
                  </Button>
                </div>
              </div>
            </div>
          )}

          {/* Link existing HPP site modal */}
          {showLinkSite && (
            <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
              <div className="bg-background rounded-xl shadow-2xl w-full max-w-sm p-6 space-y-4">
                <h3 className="font-semibold text-foreground">Vincular sitio HPP</h3>
                <p className="text-sm text-muted-foreground">
                  Selecciona un sitio sincronizado desde Hik-Partner Pro que aún no esté asignado a ninguna cuenta.
                </p>
                {loadingUnassigned ? (
                  <div className="flex justify-center py-4">
                    <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
                  </div>
                ) : unassignedSites.length === 0 ? (
                  <p className="text-sm text-muted-foreground text-center py-4">
                    No hay sitios HPP sin asignar.
                  </p>
                ) : (
                  <div className="space-y-2">
                    <div className="relative">
                      <Search className="absolute left-2.5 top-2.5 w-4 h-4 text-muted-foreground pointer-events-none" />
                      <Input
                        placeholder="Buscar sitio…"
                        className="pl-8 h-9 text-sm"
                        value={linkSearch}
                        onChange={(e) => setLinkSearch(e.target.value)}
                      />
                    </div>
                    <div>
                      <label className="text-sm font-medium text-foreground">Sitio HPP *</label>
                      <select
                        className="mt-1 w-full rounded-md border border-input bg-background px-3 py-2 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
                        size={Math.min(6, unassignedSites.filter(s => !linkSearch || s.name.toLowerCase().includes(linkSearch.toLowerCase()) || (s.address ?? '').toLowerCase().includes(linkSearch.toLowerCase())).length + 1)}
                        value={linkSiteId}
                        onChange={(e) => setLinkSiteId(e.target.value)}
                      >
                        <option value="">Selecciona un sitio…</option>
                        {unassignedSites
                          .filter((s) => !linkSearch || s.name.toLowerCase().includes(linkSearch.toLowerCase()) || (s.address ?? '').toLowerCase().includes(linkSearch.toLowerCase()))
                          .map((s) => (
                            <option key={s.id} value={s.id}>
                              {s.name}{s.address ? ` — ${s.address}` : ''}
                            </option>
                          ))}
                      </select>
                    </div>
                  </div>
                )}
                {linkError && <p className="text-sm text-red-500 bg-red-500/10 rounded-lg px-3 py-2">{linkError}</p>}
                <div className="flex gap-2">
                  <Button variant="outline" className="flex-1" onClick={() => { setShowLinkSite(false); setLinkSearch(''); }}>Cancelar</Button>
                  <Button
                    className="flex-1"
                    disabled={!linkSiteId || linkingState || loadingUnassigned}
                    onClick={handleLinkSite}
                  >
                    {linkingState ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Link2 className="w-4 h-4 mr-1" />}
                    Vincular
                  </Button>
                </div>
              </div>
            </div>
          )}

          {/* Add HPP panel to existing site modal */}
          {showAddPanel && (
            <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
              <div className="bg-background rounded-xl shadow-2xl w-full max-w-sm p-6 space-y-4">
                <h3 className="font-semibold text-foreground">Agregar panel HPP a sitio</h3>
                <p className="text-sm text-muted-foreground">
                  Selecciona un panel de HPP (aún sin cuenta) y el sitio de esta cuenta donde quedará asignado.
                </p>
                {loadingPanels ? (
                  <div className="flex justify-center py-4">
                    <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
                  </div>
                ) : availablePanels.length === 0 ? (
                  <p className="text-sm text-muted-foreground text-center py-4">
                    No hay paneles HPP disponibles para asignar.
                  </p>
                ) : (
                  <div className="space-y-3">
                    <div>
                      <label className="text-sm font-medium text-foreground">Panel HPP *</label>
                      <div className="relative mt-1">
                        <Search className="absolute left-2.5 top-2.5 w-4 h-4 text-muted-foreground pointer-events-none" />
                        <Input
                          placeholder="Buscar panel…"
                          className="pl-8 h-9 text-sm mb-1"
                          value={addPanelSearch}
                          onChange={(e) => setAddPanelSearch(e.target.value)}
                        />
                      </div>
                      <select
                        className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
                        size={Math.min(5, availablePanels.filter(p => !addPanelSearch || p.name.toLowerCase().includes(addPanelSearch.toLowerCase()) || p.serial_number.toLowerCase().includes(addPanelSearch.toLowerCase())).length + 1)}
                        value={addPanelId}
                        onChange={(e) => setAddPanelId(e.target.value)}
                      >
                        <option value="">Selecciona un panel…</option>
                        {availablePanels
                          .filter((p) => !addPanelSearch || p.name.toLowerCase().includes(addPanelSearch.toLowerCase()) || p.serial_number.toLowerCase().includes(addPanelSearch.toLowerCase()))
                          .map((p) => (
                            <option key={p.id} value={p.id}>
                              {p.name} ({p.serial_number}){p.site_name ? ` · ${p.site_name}` : ''}
                            </option>
                          ))}
                      </select>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-foreground">Sitio destino *</label>
                      <select
                        className="mt-1 w-full rounded-md border border-input bg-background px-3 py-2 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
                        value={addPanelSiteId}
                        onChange={(e) => setAddPanelSiteId(e.target.value)}
                      >
                        <option value="">Selecciona un sitio…</option>
                        {sites.map((s) => (
                          <option key={s.id} value={s.id}>{s.name}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                )}
                {addPanelError && <p className="text-sm text-red-500 bg-red-500/10 rounded-lg px-3 py-2">{addPanelError}</p>}
                <div className="flex gap-2">
                  <Button variant="outline" className="flex-1" onClick={() => setShowAddPanel(false)}>Cancelar</Button>
                  <Button
                    className="flex-1"
                    disabled={!addPanelId || !addPanelSiteId || addingPanel || loadingPanels}
                    onClick={handleAddPanel}
                  >
                    {addingPanel ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Cpu className="w-4 h-4 mr-1" />}
                    Asignar
                  </Button>
                </div>
              </div>
            </div>
          )}
        </TabsContent>

        {/* ── Tab: Miembros ── */}
        <TabsContent value="members" className="mt-4 space-y-3">
          {capacity && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {capacity.site_pool && (
                <Card className={capacity.site_pool.used >= capacity.site_pool.cap ? 'border-red-300' : ''}>
                  <CardContent className="py-3 flex items-center justify-between gap-2">
                    <div>
                      <p className="text-xs text-muted-foreground">Acceso a sitios (cuenta)</p>
                      <p className="text-lg font-semibold">
                        {capacity.site_pool.used} / {capacity.site_pool.cap}
                      </p>
                    </div>
                    <div className="flex items-center gap-2">
                      {capacity.site_pool.used >= capacity.site_pool.cap && (
                        <span className="text-xs text-red-600 font-medium">Límite alcanzado</span>
                      )}
                      <PermissionGate require="accounts.manage">
                        <Button size="sm" variant="outline" onClick={openBuyCapacityAddon}>
                          Comprar ampliación
                        </Button>
                      </PermissionGate>
                    </div>
                  </CardContent>
                </Card>
              )}
              <Card className={
                capacity.license_pool.cap !== null && capacity.license_pool.used >= capacity.license_pool.cap
                  ? 'border-red-300' : ''
              }>
                <CardContent className="py-3 flex items-center justify-between">
                  <div>
                    <p className="text-xs text-muted-foreground">Licencias de alertas por app</p>
                    <p className="text-lg font-semibold">
                      {capacity.license_pool.used} / {capacity.license_pool.cap ?? '—'}
                    </p>
                  </div>
                  {capacity.license_pool.cap !== null && capacity.license_pool.used >= capacity.license_pool.cap && (
                    <span className="text-xs text-red-600 font-medium">Límite alcanzado</span>
                  )}
                </CardContent>
              </Card>
            </div>
          )}

          <div className="flex justify-end">
            <PermissionGate require="accounts.manage">
              <Button
                size="sm"
                onClick={() => {
                  loadUsers();
                  setMemberSearch('');
                  setMemberUserId('');
                  // Default both on when there's room; the owner can uncheck either.
                  const managedHasRoom = !capacity?.is_managed || capacity.site_pool == null
                    || capacity.site_pool.used < capacity.site_pool.cap;
                  const licenseHasRoom = !capacity || capacity.license_pool.cap == null
                    || capacity.license_pool.used < capacity.license_pool.cap;
                  setMemberHasManagedAccess(managedHasRoom);
                  setMemberHasAppLicense(licenseHasRoom);
                  setShowAddMember(true);
                }}
              >
                <UserPlus className="w-4 h-4 mr-1" />
                Agregar miembro
              </Button>
            </PermissionGate>
          </div>

          {(account.members ?? []).length === 0 ? (
            <Card>
              <CardContent className="py-10 text-center">
                <Users className="w-10 h-10 text-muted-foreground mx-auto mb-2 opacity-30" />
                <p className="text-muted-foreground text-sm">Sin miembros registrados.</p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-2">
              {(account.members ?? []).map((m) => {
                const roleCfg = MEMBER_ROLE_CONFIG[m.role];
                return (
                  <div key={m.id} className="flex items-center gap-4 p-4 rounded-xl border border-border bg-card">
                    <div className="w-9 h-9 rounded-full bg-muted flex items-center justify-center text-sm font-medium text-muted-foreground shrink-0">
                      {m.profile?.full_name?.charAt(0)?.toUpperCase() ?? '?'}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-foreground truncate">{m.profile?.full_name ?? 'Usuario'}</p>
                      <p className="text-xs text-muted-foreground truncate">{m.profile?.email}</p>
                    </div>
                    <Badge className={cn('text-xs flex items-center gap-1', roleCfg.color)}>
                      {roleCfg.icon}{roleCfg.label}
                    </Badge>
                    <PermissionGate require="accounts.manage">
                      <Button
                        variant="ghost"
                        size="sm"
                        title="Delegar dispositivos y funciones"
                        onClick={() => handleOpenDelegate(m)}
                      >
                        <SlidersHorizontal className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="text-destructive hover:text-destructive"
                        onClick={() => handleRemoveMember(m.id)}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </PermissionGate>
                  </div>
                );
              })}
            </div>
          )}

          {/* Add member modal */}
          {showAddMember && (
            <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
              <div className="bg-background rounded-xl shadow-2xl w-full max-w-sm p-6 space-y-4">
                <h3 className="font-semibold text-foreground">Agregar miembro</h3>
                <div>
                  <label className="text-sm font-medium text-foreground">Usuario</label>
                  <Input
                    className="mt-1"
                    placeholder="Buscar por nombre o correo..."
                    value={memberSearch}
                    onChange={(e) => { setMemberSearch(e.target.value); setMemberUserId(''); }}
                  />                  {memberSearch.trim() && (
                    <div className="mt-1 max-h-48 overflow-y-auto rounded-md border border-border bg-background shadow-sm">
                      {allUsers
                        .filter((u) => {
                          const q = memberSearch.toLowerCase();
                          return u.full_name.toLowerCase().includes(q) || u.email.toLowerCase().includes(q);
                        })
                        .map((u) => (
                          <button
                            key={u.id}
                            type="button"
                            onClick={() => { setMemberUserId(u.id); setMemberSearch(`${u.full_name} (${u.email})`); }}
                            className={`w-full text-left px-3 py-2 text-sm hover:bg-muted transition-colors ${
                              memberUserId === u.id ? 'bg-primary/10 text-primary' : 'text-foreground'
                            }`}
                          >
                            <span className="font-medium">{u.full_name}</span>
                            <span className="text-muted-foreground ml-1.5 text-xs">{u.email}</span>
                          </button>
                        ))}
                    </div>
                  )}
                  {memberUserId && (
                    <p className="mt-1 text-xs text-green-600 dark:text-green-400">Usuario seleccionado ✓</p>
                  )}
                </div>
                {capacity?.is_managed && (
                  <div>
                    <label className="text-sm font-medium text-foreground">Tipo de acceso (elige uno o ambos)</label>
                    <button
                      type="button"
                      onClick={() => setMemberHasManagedAccess((v) => !v)}
                      className="mt-2 flex items-center gap-2 text-left"
                    >
                      <span className={cn(
                        'w-4 h-4 rounded border flex items-center justify-center flex-shrink-0',
                        memberHasManagedAccess ? 'bg-primary border-primary' : 'border-border'
                      )}>
                        {memberHasManagedAccess && <Check className="w-3 h-3 text-primary-foreground" />}
                      </span>
                      <span className="text-sm text-foreground">Cuenta gestionada (cámaras/paneles)</span>
                    </button>
                    <p className={cn(
                      'mt-1 text-xs font-medium ml-6',
                      !memberHasManagedAccess
                        ? 'text-muted-foreground'
                        : (capacity.site_pool != null && capacity.site_pool.used >= capacity.site_pool.cap)
                          ? 'text-red-600 dark:text-red-400'
                          : 'text-green-600 dark:text-green-400'
                    )}>
                      {memberHasManagedAccess
                        ? `Usa ${capacity.site_pool?.used ?? 0}/${capacity.site_pool?.cap ?? '∞'} cupos de cuenta gestionada.`
                        : 'No tendrá acceso a cámaras/paneles del sistema de alarma.'}
                    </p>

                    <button
                      type="button"
                      onClick={() => setMemberHasAppLicense((v) => !v)}
                      className="mt-3 flex items-center gap-2 text-left"
                    >
                      <span className={cn(
                        'w-4 h-4 rounded border flex items-center justify-center flex-shrink-0',
                        memberHasAppLicense ? 'bg-primary border-primary' : 'border-border'
                      )}>
                        {memberHasAppLicense && <Check className="w-3 h-3 text-primary-foreground" />}
                      </span>
                      <span className="text-sm text-foreground">Alertas SOS/pánico desde la app</span>
                    </button>
                    <p className={cn(
                      'mt-1 text-xs font-medium ml-6',
                      !memberHasAppLicense
                        ? 'text-muted-foreground'
                        : (capacity.license_pool.cap != null && capacity.license_pool.used >= capacity.license_pool.cap)
                          ? 'text-red-600 dark:text-red-400'
                          : 'text-green-600 dark:text-green-400'
                    )}>
                      {memberHasAppLicense
                        ? `Usa ${capacity.license_pool.used}/${capacity.license_pool.cap ?? '∞'} licencias de alertas por app.`
                        : 'No podrá enviar SOS/pánico desde la app.'}
                    </p>
                  </div>
                )}
                <div>
                  <label className="text-sm font-medium text-foreground">Rol en la cuenta</label>
                  <div className="grid grid-cols-2 gap-2 mt-1">
                    {(Object.keys(MEMBER_ROLE_CONFIG) as AccountMemberRole[]).map((r) => (
                      <button
                        key={r}
                        type="button"
                        onClick={() => setMemberRole(r)}
                        className={cn(
                          'flex items-center gap-1 px-3 py-2 rounded-lg text-xs font-medium border transition-colors',
                          memberRole === r
                            ? 'border-primary bg-primary/10 text-primary'
                            : 'border-border text-muted-foreground hover:bg-muted'
                        )}
                      >
                        {MEMBER_ROLE_CONFIG[r].icon}
                        {MEMBER_ROLE_CONFIG[r].label}
                      </button>
                    ))}
                  </div>
                </div>
                {memberError && (
                  <p className="text-sm text-destructive bg-destructive/10 rounded-lg px-3 py-2">{memberError}</p>
                )}
                <div className="flex gap-2">
                  <Button variant="outline" className="flex-1" onClick={() => { setShowAddMember(false); setMemberError(null); }}>Cancelar</Button>
                  <Button
                    className="flex-1"
                    disabled={!memberUserId || addingMember}
                    onClick={handleAddMember}
                  >
                    {addingMember ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Agregar'}
                  </Button>
                </div>
              </div>
            </div>
          )}
        </TabsContent>

        {/* ── Tab: Receptores de alertas ── */}
        <TabsContent value="recipients" className="mt-4">
          <AlertRecipientsTab accountId={account.id} members={account.members ?? []} />
        </TabsContent>

        {/* ── Tab: Invitaciones ── */}
        <TabsContent value="invites" className="mt-4">
          {sites.length > 1 && (
            <div className="mb-4 flex items-center gap-2">
              <label className="text-sm text-muted-foreground shrink-0">Sitio/panel:</label>
              <select
                className="flex-1 max-w-sm rounded-md border border-border bg-background px-3 py-1.5 text-sm"
                value={qrSiteId || sites[0].id}
                onChange={(e) => setQrSiteId(e.target.value)}
              >
                {sites.map((s) => (
                  <option key={s.id} value={s.id}>{s.name}</option>
                ))}
              </select>
            </div>
          )}
          <div className="grid grid-cols-1 xl:grid-cols-2 gap-6 items-stretch">
            {sites.length === 0 ? (
              <Card>
                <CardContent className="py-10 text-center">
                  <p className="text-muted-foreground text-sm">
                    Crea un sitio en la pestaña "Sitios" antes de generar el código QR del panel.
                  </p>
                </CardContent>
              </Card>
            ) : (() => {
              const site = sites.find((s) => s.id === (qrSiteId || sites[0].id)) ?? sites[0];
              return <AdminOnboardingQR key={site.id} accountId={account.id} siteId={site.id} siteName={site.name} />;
            })()}
            <InvitesPage accountId={account.id} embedded />
          </div>
        </TabsContent>

        {/* ── Tab: Facturación ── */}
        <TabsContent value="billing" className="mt-4 space-y-4">

          {/* ── Suscripciones / Plan activo ──────────────────────────────────── */}
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-semibold text-foreground flex items-center gap-2">
              <Repeat className="w-4 h-4 text-primary" />
              Plan / Suscripciones
            </h3>
            <PermissionGate require="accounts.manage">
              <Button size="sm" onClick={openCreateSub}>
                <Plus className="w-4 h-4 mr-1" /> Agregar plan
              </Button>
            </PermissionGate>
          </div>

          {subscriptions.length === 0 ? (
            <Card>
              <CardContent className="py-6 text-center">
                <Repeat className="w-8 h-8 text-muted-foreground mx-auto mb-2 opacity-30" />
                <p className="text-sm text-muted-foreground">Sin plan configurado.</p>
                <p className="text-xs text-muted-foreground mt-1">
                  Agrega una suscripción para generar facturas automáticas desde Facturación por lote.
                </p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-2">
              {subscriptions.map((sub) => {
                const product = billingProducts.find((p) => p.id === sub.product_id);
                const unitPrice = sub.unit_price_override ?? product?.default_unit_price ?? 0;
                const total = unitPrice * sub.quantity;
                const intervalLabel: Record<string, string> = { monthly: 'mes', quarterly: 'trimestre', yearly: 'año' };
                const statusColor: Record<string, string> = {
                  active:    'bg-green-500/10 text-green-600',
                  paused:    'bg-yellow-500/10 text-yellow-600',
                  cancelled: 'bg-gray-500/10 text-gray-500',
                };
                const statusLabel: Record<string, string> = { active: 'Activa', paused: 'Pausada', cancelled: 'Cancelada' };
                const subSite = sub.site_id ? sites.find((s) => s.id === sub.site_id) : null;
                return (
                  <Card key={sub.id} className={sub.status !== 'active' ? 'opacity-60' : ''}>
                    <div className="flex items-center gap-4 px-4 py-3">
                      <Repeat className="w-4 h-4 text-muted-foreground shrink-0" />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <p className="text-sm font-medium text-foreground">
                            {product?.name ?? 'Producto desconocido'}
                          </p>
                          <Badge className={cn('text-xs', statusColor[sub.status] ?? '')}>{statusLabel[sub.status] ?? sub.status}</Badge>
                        </div>
                        <p className="text-xs text-muted-foreground mt-0.5">
                          <span className="font-medium text-foreground">Q{total.toFixed(2)}</span>
                          /{intervalLabel[sub.billing_interval] ?? sub.billing_interval}
                          {' · '}Cobra día {sub.billing_day}
                          {' · '}Próximo: {sub.next_billing_date}
                          {subSite ? ` · Sitio: ${subSite.name}` : ''}
                        </p>
                      </div>
                      <PermissionGate require="accounts.manage">
                        <Button variant="ghost" size="sm" onClick={() => openEditSub(sub)}>
                          <Pencil className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="text-destructive hover:text-destructive"
                          onClick={() => handleDeleteSub(sub.id)}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </PermissionGate>
                    </div>
                  </Card>
                );
              })}
              {/* Summary card */}
              {subscriptions.filter((s) => s.status === 'active').length > 0 && (() => {
                const monthlyTotal = subscriptions
                  .filter((s) => s.status === 'active')
                  .reduce((acc, sub) => {
                    const product = billingProducts.find((p) => p.id === sub.product_id);
                    const unitPrice = sub.unit_price_override ?? product?.default_unit_price ?? 0;
                    const amount = unitPrice * sub.quantity;
                    if (sub.billing_interval === 'monthly')    return acc + amount;
                    if (sub.billing_interval === 'quarterly')  return acc + amount / 3;
                    if (sub.billing_interval === 'yearly')     return acc + amount / 12;
                    return acc;
                  }, 0);
                return (
                  <div className="flex items-center justify-end gap-2 px-1 pt-1">
                    <p className="text-xs text-muted-foreground">Total mensual estimado:</p>
                    <p className="text-sm font-bold text-foreground">Q{monthlyTotal.toFixed(2)}</p>
                  </div>
                );
              })()}
            </div>
          )}

          {/* ── Resumen de facturación ────────────────────────────────────── */}
          {account.billing_email && (
            <p className="text-sm text-muted-foreground">
              Facturas a: <span className="text-foreground">{account.billing_email}</span>
              {' · '}Día base: {account.billing_day}
            </p>
          )}

          {/* ── Facturas ──────────────────────────────────────────────────── */}
          <h3 className="text-sm font-semibold text-foreground flex items-center gap-2 pt-2">
            <FileText className="w-4 h-4 text-primary" />
            Facturas
          </h3>

          {invoices.length === 0 ? (
            <Card>
              <CardContent className="py-10 text-center">
                <FileText className="w-10 h-10 text-muted-foreground mx-auto mb-2 opacity-30" />
                <p className="text-sm text-muted-foreground">Sin facturas registradas.</p>
              </CardContent>
            </Card>
          ) : (
            <Card>
              <div className="divide-y divide-border">
                {invoices.map((inv) => {
                  const sc = INVOICE_STATUS_CONFIG[inv.status] ?? { label: inv.status, color: '' };
                  return (
                    <div key={inv.id} className="flex items-center gap-4 px-5 py-3">
                      <FileText className="w-4 h-4 text-muted-foreground shrink-0" />
                      <div className="flex-1">
                        <p className="text-sm font-medium text-foreground">{inv.invoice_number}</p>
                        <p className="text-xs text-muted-foreground">
                          {inv.due_date ? `Vence: ${new Date(inv.due_date).toLocaleDateString('es-GT')}` : ''}
                          {inv.description ? ` · ${inv.description}` : ''}
                        </p>
                        {inv.fel_uuid && (
                          <p className="text-[11px] text-emerald-600 dark:text-emerald-400 font-mono mt-0.5">
                            FEL: {inv.fel_uuid}
                          </p>
                        )}
                      </div>
                      <Badge className={cn('text-xs', sc.color)}>{sc.label}</Badge>
                      <p className="text-sm font-semibold text-foreground shrink-0">
                        {inv.currency} {inv.amount.toFixed(2)}
                      </p>
                      <InvoiceFelActions invoice={inv} accountId={account.id} accountFel={{
                        fel_nit:         (account as any).fel_nit         ?? null,
                        fel_legal_name:  (account as any).fel_legal_name  ?? null,
                        fel_address:     (account as any).fel_address     ?? null,
                        fel_email_invoice: (account as any).fel_email_invoice ?? null,
                        billing_name:    account.billing_name             ?? null,
                        billing_email:   account.billing_email            ?? null,
                        billing_address: account.billing_address          ?? null,
                        accountName:     account.name,
                      }} onEmitted={async () => {
                        const fresh = await fetchInvoicesByAccount(account.id);
                        setInvoices(fresh);
                      }} onVoided={(id) =>
                        setInvoices((prev) =>
                          prev.map((x) => (x.id === id ? { ...x, status: 'void' } : x)),
                        )
                      } />
                    </div>
                  );
                })}
              </div>
            </Card>
          )}
        </TabsContent>

        {/* ── Tab: Servicios / Feature Flags ── */}
        <TabsContent value="services" className="mt-4">

          {/* ── Suspensión rápida por no pago ── */}
          {(() => {
            const isSuspended = !(account as any).feature_cameras && !(account as any).feature_panels && !(account as any).feature_patrols;
            const allActive = ((account as any).feature_cameras ?? true) && ((account as any).feature_panels ?? true) && ((account as any).feature_patrols ?? true);
            return (
              <Card className={`mb-4 ${isSuspended ? 'border-red-400/50 bg-red-500/5' : ''}`}>
                <CardHeader className="pb-2">
                  <CardTitle className="text-base flex items-center gap-2">
                    {isSuspended
                      ? <><span className="w-2 h-2 rounded-full bg-red-500 animate-pulse" /> Cuenta suspendida por no pago</>
                      : <><span className="w-2 h-2 rounded-full bg-green-500" /> Control de acceso por pago</>
                    }
                  </CardTitle>
                  <p className="text-sm text-muted-foreground">
                    Acciones rápidas para suspender o reactivar los servicios del cliente sin modificar los equipos ni el contrato.
                  </p>
                </CardHeader>
                <CardContent>
                  <PermissionGate require="accounts.manage">
                    <div className="flex flex-wrap gap-2">
                      {!isSuspended && (
                        <button
                          type="button"
                          disabled={savingFeatures}
                          onClick={async () => {
                            if (!confirm('¿Suspender cámaras, armado/desarmado y patrullas por no pago? El monitoreo ARC continúa activo.')) return;
                            setSavingFeatures(true);
                            try {
                              await updateAccountFeatures(account.id, {
                                feature_cameras: false,
                                feature_panels: false,
                                feature_patrols: false,
                              });
                              await load();
                            } finally { setSavingFeatures(false); }
                          }}
                          className="flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium bg-red-500/10 text-red-600 border border-red-300/50 hover:bg-red-500/20 transition-colors disabled:opacity-50"
                        >
                          {savingFeatures ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <span>⏸</span>}
                          Suspender por no pago
                        </button>
                      )}
                      {isSuspended && (
                        <button
                          type="button"
                          disabled={savingFeatures}
                          onClick={async () => {
                            setSavingFeatures(true);
                            try {
                              await updateAccountFeatures(account.id, {
                                feature_cameras: true,
                                feature_panels: true,
                                feature_patrols: true,
                                feature_arc: true,
                              });
                              await load();
                            } finally { setSavingFeatures(false); }
                          }}
                          className="flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium bg-green-500/10 text-green-600 border border-green-300/50 hover:bg-green-500/20 transition-colors disabled:opacity-50"
                        >
                          {savingFeatures ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <span>▶</span>}
                          Reactivar todos los servicios
                        </button>
                      )}
                      {!allActive && !isSuspended && (
                        <button
                          type="button"
                          disabled={savingFeatures}
                          onClick={async () => {
                            setSavingFeatures(true);
                            try {
                              await updateAccountFeatures(account.id, {
                                feature_cameras: true,
                                feature_panels: true,
                                feature_patrols: true,
                                feature_arc: true,
                              });
                              await load();
                            } finally { setSavingFeatures(false); }
                          }}
                          className="flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium bg-primary/10 text-primary border border-primary/20 hover:bg-primary/20 transition-colors disabled:opacity-50"
                        >
                          {savingFeatures ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <span>✓</span>}
                          Reactivar todos
                        </button>
                      )}
                    </div>
                    {isSuspended && (
                      <p className="text-xs text-red-500/80 mt-2">
                        El monitoreo ARC continúa activo. El cliente no puede ver cámaras, armar/desarmar ni solicitar patrulla hasta reactivar.
                      </p>
                    )}
                  </PermissionGate>
                </CardContent>
              </Card>
            );
          })()}

          <Card>
            <CardHeader>
              <CardTitle className="text-base">Funciones habilitadas</CardTitle>
              <p className="text-sm text-muted-foreground">
                Desactiva funciones para cuentas que no estén al corriente en su pago.
                Los cambios aplican inmediatamente en la app del cliente.
              </p>
            </CardHeader>
            <CardContent className="space-y-3">
              {([
                { flag: 'feature_cameras' as keyof AccountFeatures, label: 'Cámaras en vivo', desc: 'El cliente puede ver transmisiones en vivo', icon: Video },
                { flag: 'feature_panels' as keyof AccountFeatures, label: 'Paneles de alarma', desc: 'El cliente puede ver y armar/desarmar paneles', icon: Radio },
                { flag: 'feature_arc' as keyof AccountFeatures, label: 'Monitoreo ARC', desc: 'La cuenta recibe atención del Centro de Recepción', icon: Activity },
                { flag: 'feature_patrols' as keyof AccountFeatures, label: 'Patrulla a domicilio', desc: 'El cliente puede solicitar despacho de patrulla', icon: Navigation },
                { flag: 'feature_ble_button' as keyof AccountFeatures, label: 'Botón físico BLE', desc: 'Habilita el botón de pánico físico Minew B10 para este cliente', icon: Radio },
              ] as const).map(({ flag, label, desc, icon: Icon }) => {
                const enabled = (account as any)[flag] ?? true;
                return (
                  <div
                    key={flag}
                    className={`flex items-center gap-4 px-4 py-3 rounded-xl border transition-colors ${enabled ? 'border-border bg-card' : 'border-red-300/50 bg-red-500/5'}`}
                  >
                    <div className={`w-9 h-9 rounded-lg flex items-center justify-center shrink-0 ${enabled ? 'bg-primary/10' : 'bg-red-500/10'}`}>
                      <Icon className={`w-5 h-5 ${enabled ? 'text-primary' : 'text-red-500'}`} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-sm text-foreground">{label}</p>
                      <p className="text-xs text-muted-foreground">{desc}</p>
                    </div>
                    <PermissionGate require="accounts.manage">
                      <button
                        type="button"
                        disabled={savingFeatures}
                        onClick={() => handleToggleFeature(flag)}
                        className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none ${enabled ? 'bg-primary' : 'bg-muted-foreground/30'}`}
                      >
                        <span
                          className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${enabled ? 'translate-x-6' : 'translate-x-1'}`}
                        />
                      </button>
                    </PermissionGate>
                  </div>
                );
              })}
              {!account.is_active && (
                <div className="mt-3 px-4 py-3 rounded-xl bg-orange-500/5 border border-orange-300/40 text-xs text-orange-600">
                  Esta cuenta está marcada como inactiva. Considerar desactivar todos los servicios.
                </div>
              )}
            </CardContent>
          </Card>

          {/* Patrol radius configurator */}
          {account.feature_patrols && (
            <Card>
              <CardHeader>
                <CardTitle className="text-base flex items-center gap-2">
                  <Navigation className="w-4 h-4" />
                  Radio de patrullaje
                </CardTitle>
                <p className="text-sm text-muted-foreground">
                  Distancia máxima en kilómetros para la asignación automática de patrulla
                  al recibir una alerta. El patrullero más cercano dentro de este radio
                  recibe la alerta al instante (incluso si su app está en segundo plano).
                </p>
              </CardHeader>
              <CardContent>
                <PermissionGate require="accounts.manage">
                  <div className="flex items-center gap-4">
                    <input
                      type="range"
                      min={1}
                      max={100}
                      step={1}
                      value={patrolRadiusInput}
                      onChange={(e) => setPatrolRadiusInput(Number(e.target.value))}
                      onFocus={() => setPatrolRadiusInput(account.patrol_radius_km ?? 15)}
                      className="flex-1 accent-primary"
                    />
                    <div className="flex items-center gap-2">
                      <Input
                        type="number"
                        min={1}
                        max={100}
                        value={patrolRadiusInput}
                        onChange={(e) => setPatrolRadiusInput(Number(e.target.value))}
                        className="w-20 text-center"
                      />
                      <span className="text-sm text-muted-foreground whitespace-nowrap">km</span>
                    </div>
                    <Button
                      size="sm"
                      disabled={savingRadius || patrolRadiusInput === (account.patrol_radius_km ?? 15)}
                      onClick={handleSavePatrolRadius}
                    >
                      {savingRadius ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Guardar'}
                    </Button>
                  </div>
                  <p className="mt-2 text-xs text-muted-foreground">
                    Radio actual: <strong>{account.patrol_radius_km ?? 15} km</strong>
                  </p>
                </PermissionGate>
              </CardContent>
            </Card>
          )}

          {/* ── Modo de registro ── */}
          {(() => {
            const regSrc = (account as unknown as Record<string, unknown>).registration_source as string | null | undefined;
            const isSelf = regSrc === 'self_service';
            return (
              <Card className="mt-4">
                <CardHeader>
                  <CardTitle className="text-base flex items-center gap-2">
                    Modo de registro
                    <Badge variant="outline" className={isSelf ? 'text-amber-600 border-amber-400' : 'text-green-600 border-green-400'}>
                      {isSelf ? 'Solo app (auto-registro)' : 'Cuenta ASI gestionada'}
                    </Badge>
                  </CardTitle>
                  <p className="text-sm text-muted-foreground">
                    {isSelf
                      ? 'El cliente se registró por cuenta propia. La app oculta la tab Dispositivos hasta que se promueva a cuenta gestionada.'
                      : 'La cuenta está gestionada por ASI. El cliente ve la tab Dispositivos en su app.'}
                  </p>
                </CardHeader>
                <CardContent className="flex gap-2">
                  {!isSelf && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={async () => {
                        if (!confirm('¿Revertir esta cuenta a modo "solo app"? El cliente perderá acceso a la tab Dispositivos.')) return;
                        await updateAccount(account.id, { registration_source: 'self_service' });
                        setAccount((prev) => prev ? { ...prev, registration_source: 'self_service' } as typeof prev : prev);
                      }}
                    >
                      Revertir a solo app
                    </Button>
                  )}
                  {isSelf && (
                    <Button
                      size="sm"
                      onClick={async () => {
                        await updateAccount(account.id, { registration_source: 'asi_managed' });
                        setAccount((prev) => prev ? { ...prev, registration_source: 'asi_managed' } as typeof prev : prev);
                      }}
                    >
                      Promover a cuenta gestionada
                    </Button>
                  )}
                </CardContent>
              </Card>
            );
          })()}
        </TabsContent>
        <TabsContent value="info" className="mt-4">
          {!editing ? (
            <Card>
              <CardContent className="py-5 space-y-3">
                <Row label="Nombre" value={account.name} />
                <Row label="Tipo" value={TYPE_CONFIG[account.type].label} />
                <Row label="NIT" value={account.nit} />
                <Row label="Teléfono" value={account.phone} />
                <Row label="Dirección" value={account.address} />
                <Row label="Departamento" value={departments.find((d) => d.id === account.department_id)?.name ?? (account.department_id ? String(account.department_id) : null)} />
                <Row label="Municipio" value={municipalities.find((m) => m.id === account.municipality_id)?.name ?? (account.municipality_id ? String(account.municipality_id) : null)} />
                <Row label="Correo de facturación" value={account.billing_email} />
                <Row label="Nombre fiscal" value={account.billing_name} />
                <Row label="Dirección fiscal" value={account.billing_address} />
                <Row label="Mensualidad" value={account.monthly_fee != null ? `Q${account.monthly_fee.toFixed(2)}/mes` : null} />
                <Row label="Estado" value={account.is_active ? 'Activa' : 'Inactiva (alertas bloqueadas)'} />
                <Row label="Modo" value={account.is_demo ? 'Demostración (no cuenta en ingresos)' : 'Producción'} />
                {account.notes && <Row label="Notas" value={account.notes} />}
              </CardContent>
            </Card>
          ) : (
            <Card>
              <CardContent className="py-5 space-y-3">
                <div className="grid grid-cols-2 gap-3">
                  <div className="col-span-2">
                    <label className="text-sm font-medium text-foreground">Nombre</label>
                    <Input value={editForm.name ?? ''} onChange={(e) => setEditForm((f: Partial<AccountWithMembers>) => ({ ...f, name: e.target.value }))} className="mt-1" />
                  </div>
                  <div>
                    <label className="text-sm font-medium text-foreground">NIT / CUI (DPI)</label>
                    <div className="flex gap-2 mt-1">
                      <Input
                        placeholder="CF, 1234567-8 o 1234567890101 (CUI)"
                        value={editForm.nit ?? ''}
                        onChange={(e) => {
                          setEditForm((f: Partial<AccountWithMembers>) => ({ ...f, nit: e.target.value }));
                          setNitLookup({ loading: false, result: null, error: null });
                        }}
                        className="flex-1"
                      />
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={handleLookupNit}
                        disabled={nitLookup.loading || !(editForm.nit ?? '').trim()}
                        title="Detecta autom\u00e1ticamente NIT o CUI (13 d\u00edgitos) y consulta a INFILE"
                      >
                        {nitLookup.loading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Verificar'}
                      </Button>
                    </div>
                    {nitLookup.result && (
                      <p className="text-xs text-green-600 dark:text-green-400 mt-1">{nitLookup.result}</p>
                    )}
                    {nitLookup.error && (
                      <p className="text-xs text-red-500 mt-1">{nitLookup.error}</p>
                    )}
                  </div>
                  <div>
                    <label className="text-sm font-medium text-foreground">Teléfono</label>
                    <Input value={editForm.phone ?? ''} onChange={(e) => setEditForm((f: Partial<AccountWithMembers>) => ({ ...f, phone: e.target.value }))} className="mt-1" />
                  </div>
                  <div>
                    <label className="text-sm font-medium text-foreground">Departamento</label>
                    <select
                      value={editForm.department_id ?? ''}
                      onChange={(e) => setEditForm((f: Partial<AccountWithMembers>) => ({ ...f, department_id: e.target.value ? Number(e.target.value) : null, municipality_id: null }))}
                      className="mt-1 w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                    >
                      <option value="">— Seleccionar —</option>
                      {departments.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-foreground">Municipio</label>
                    <select
                      value={editForm.municipality_id ?? ''}
                      onChange={(e) => setEditForm((f: Partial<AccountWithMembers>) => ({ ...f, municipality_id: e.target.value ? Number(e.target.value) : null }))}
                      disabled={!editForm.department_id}
                      className="mt-1 w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring disabled:opacity-50"
                    >
                      <option value="">— Seleccionar —</option>
                      {municipalities.map((m) => <option key={m.id} value={m.id}>{m.name}</option>)}
                    </select>
                  </div>
                  <div className="col-span-2">
                    <label className="text-sm font-medium text-foreground">Dirección</label>
                    <Input placeholder="5a Av 12-34, Zona 10" value={editForm.address ?? ''} onChange={(e) => setEditForm((f: Partial<AccountWithMembers>) => ({ ...f, address: e.target.value }))} className="mt-1" />
                  </div>
                  <div>
                    <label className="text-sm font-medium text-foreground">Correo facturación</label>
                    <Input type="email" value={editForm.billing_email ?? ''} onChange={(e) => setEditForm((f: Partial<AccountWithMembers>) => ({ ...f, billing_email: e.target.value }))} className="mt-1" />
                  </div>
                  <div>
                    <label className="text-sm font-medium text-foreground">Nombre fiscal</label>
                    <Input value={editForm.billing_name ?? ''} onChange={(e) => setEditForm((f: Partial<AccountWithMembers>) => ({ ...f, billing_name: e.target.value }))} className="mt-1" />
                  </div>
                  <div className="col-span-2">
                    <label className="text-sm font-medium text-foreground">Dirección fiscal</label>
                    <Input value={editForm.billing_address ?? ''} onChange={(e) => setEditForm((f: Partial<AccountWithMembers>) => ({ ...f, billing_address: e.target.value }))} className="mt-1" />
                  </div>
                  <div>
                    <label className="text-sm font-medium text-foreground">Mensualidad (Q)</label>
                    <Input type="number" value={editForm.monthly_fee ?? ''} onChange={(e) => setEditForm((f: Partial<AccountWithMembers>) => ({ ...f, monthly_fee: parseFloat(e.target.value) || null }))} className="mt-1" />
                  </div>
                  <div>
                    <label className="text-sm font-medium text-foreground">Día de cobro</label>
                    <Input type="number" min={1} max={28} value={editForm.billing_day ?? 1} onChange={(e) => setEditForm((f: Partial<AccountWithMembers>) => ({ ...f, billing_day: parseInt(e.target.value, 10) }))} className="mt-1" />
                  </div>
                  <div className="col-span-2">
                    <label className="text-sm font-medium text-foreground">Notas</label>
                    <textarea
                      className="mt-1 w-full rounded-md border border-border bg-background px-3 py-2 text-sm text-foreground min-h-[80px] resize-none"
                      value={editForm.notes ?? ''}
                      onChange={(e) => setEditForm((f: Partial<AccountWithMembers>) => ({ ...f, notes: e.target.value }))}
                    />
                  </div>
                  <div className="col-span-2">
                    <label className="text-sm font-medium text-foreground block mb-2">Clasificación del cliente</label>
                    <div className="flex gap-2">
                      {(['A', 'B', 'C'] as AccountTier[]).map((tier) => {
                        const selected = (editForm as any).client_tier === tier;
                        return (
                          <button
                            key={tier}
                            type="button"
                            onClick={() => setEditForm((f: Partial<AccountWithMembers>) => ({ ...f, client_tier: selected ? null : tier }))}
                            className={cn(
                              'flex items-center gap-1.5 px-4 py-2 rounded-lg border text-sm font-bold transition-all',
                              selected
                                ? cn(TIER_CONFIG[tier].bg, TIER_CONFIG[tier].color, 'border-current')
                                : 'border-border text-muted-foreground hover:bg-muted',
                            )}
                          >
                            <Star className="w-3.5 h-3.5" />
                            Tier {tier}
                          </button>
                        );
                      })}
                      {(editForm as any).client_tier && (
                        <button
                          type="button"
                          onClick={() => setEditForm((f: Partial<AccountWithMembers>) => ({ ...f, client_tier: null }))}
                          className="px-3 py-2 rounded-lg border border-border text-xs text-muted-foreground hover:bg-muted"
                        >
                          Quitar
                        </button>
                      )}
                    </div>
                    <p className="text-xs text-muted-foreground mt-1.5">A = Premium · B = Estándar · C = Básico / nuevo. Futuro: clasificación automática.</p>
                  </div>
                  <div className="col-span-2">
                    <label className="flex items-start gap-3 rounded-lg border border-border bg-muted/30 px-3 py-3 cursor-pointer">
                      <input
                        type="checkbox"
                        className="mt-0.5 h-4 w-4 accent-amber-500"
                        checked={editForm.is_demo ?? false}
                        onChange={(e) => setEditForm((f: Partial<AccountWithMembers>) => ({ ...f, is_demo: e.target.checked }))}
                      />
                      <span className="text-sm">
                        <span className="font-medium text-foreground flex items-center gap-1.5">
                          <Rocket className="w-3.5 h-3.5 text-amber-500" />
                          Cuenta de demostración
                        </span>
                        <span className="text-xs text-muted-foreground block mt-1">
                          La cuenta funciona al 100% según su plan (alertas, cámaras, paneles, etc.). Solo se marca como <strong>demo</strong> para excluirla de métricas de ingresos y conteo de clientes pagados.
                          Para apagarla, usa <strong>Desactivar cuenta</strong>.
                        </span>
                      </span>
                    </label>
                  </div>
                </div>
                {error && <div className="text-sm text-red-500 bg-red-500/10 rounded-lg px-3 py-2">{error}</div>}
                <div className="flex gap-2">
                  <Button variant="outline" onClick={() => setEditing(false)}>Cancelar</Button>
                  <Button onClick={handleSave} disabled={saving}>
                    {saving ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Check className="w-4 h-4 mr-1" />}
                    Guardar
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}
        </TabsContent>
      </Tabs>

      {/* ── Subscription create/edit modal ── */}
      {showSubModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4 overflow-y-auto">
          <div className="bg-background rounded-xl shadow-2xl w-full max-w-md p-6 space-y-4 my-4">
            <h3 className="font-semibold text-foreground">
              {editingSub ? 'Editar suscripción' : 'Nueva suscripción'}
            </h3>

            <div>
              <label className="text-sm font-medium text-foreground">Producto *</label>
              <select
                value={subForm.product_id ?? ''}
                onChange={(e) => {
                  const prod = billingProducts.find((p) => p.id === e.target.value);
                  setSubForm((f) => ({
                    ...f,
                    product_id: e.target.value,
                    unit_price_override: f.unit_price_override ?? prod?.default_unit_price ?? null,
                  }));
                }}
                className="mt-1 w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
              >
                <option value="">— Selecciona un producto —</option>
                {billingProducts.filter((p) => p.is_active).map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.name} — Q{p.default_unit_price.toFixed(2)}
                  </option>
                ))}
              </select>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-sm font-medium text-foreground">Precio (Q)</label>
                <Input
                  type="number"
                  min="0"
                  step="0.01"
                  placeholder="Precio del producto"
                  value={subForm.unit_price_override ?? ''}
                  onChange={(e) => setSubForm((f) => ({ ...f, unit_price_override: e.target.value ? parseFloat(e.target.value) : null }))}
                  className="mt-1"
                />
                <p className="text-xs text-muted-foreground mt-0.5">Vacío = precio del catálogo</p>
                {/* Per-site multiplication hint: shown when no specific site is selected and account has >1 site */}
                {!subForm.site_id && sites.length > 1 && subForm.product_id && (() => {
                  const prod = billingProducts.find((p) => p.id === subForm.product_id);
                  if (!prod) return null;
                  const suggested = Math.round(prod.default_unit_price * sites.length * 100) / 100;
                  return (
                    <div className="flex items-center gap-2 mt-1.5 rounded-md bg-blue-500/10 border border-blue-500/20 px-2.5 py-1.5 text-xs text-blue-700 dark:text-blue-300">
                      <span>
                        Q{prod.default_unit_price.toFixed(2)} × {sites.length} sitios
                        {' = '}<strong>Q{suggested.toFixed(2)}</strong>
                      </span>
                      <button
                        type="button"
                        className="ml-auto underline hover:no-underline font-medium shrink-0"
                        onClick={() => setSubForm((f) => ({ ...f, unit_price_override: suggested }))}
                      >
                        Aplicar
                      </button>
                    </div>
                  );
                })()}
              </div>
              <div>
                <label className="text-sm font-medium text-foreground">Día de cobro</label>
                <Input
                  type="number"
                  min={1}
                  max={28}
                  value={subForm.billing_day ?? 1}
                  onChange={(e) => setSubForm((f) => ({ ...f, billing_day: parseInt(e.target.value, 10) }))}
                  className="mt-1"
                />
              </div>
            </div>

            <div>
              <label className="text-sm font-medium text-foreground">Cantidad</label>
              <Input
                type="number"
                min={1}
                step="1"
                value={subForm.quantity ?? 1}
                onChange={(e) => setSubForm((f) => ({ ...f, quantity: Math.max(1, parseInt(e.target.value, 10) || 1) }))}
                className="mt-1"
              />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-sm font-medium text-foreground">Frecuencia</label>
                <select
                  value={subForm.billing_interval ?? 'monthly'}
                  onChange={(e) => setSubForm((f) => ({ ...f, billing_interval: e.target.value as BillingInterval }))}
                  className="mt-1 w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                >
                  <option value="monthly">Mensual</option>
                  <option value="quarterly">Trimestral</option>
                  <option value="yearly">Anual</option>
                </select>
              </div>
              <div>
                <label className="text-sm font-medium text-foreground">Estado</label>
                <select
                  value={subForm.status ?? 'active'}
                  onChange={(e) => setSubForm((f) => ({ ...f, status: e.target.value as SubscriptionStatus }))}
                  className="mt-1 w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                >
                  <option value="active">Activa</option>
                  <option value="paused">Pausada</option>
                  <option value="cancelled">Cancelada</option>
                </select>
              </div>
            </div>

            <div>
              <label className="text-sm font-medium text-foreground">Próxima fecha de cobro</label>
              <DatePickerDDMMYYYY
                value={subForm.next_billing_date ?? ''}
                onChange={(v) => setSubForm((f) => ({ ...f, next_billing_date: v }))}
              />
            </div>

            <div>
              <label className="text-sm font-medium text-foreground">Sitio (opcional)</label>
              <select
                value={subForm.site_id ?? ''}
                onChange={(e) => setSubForm((f) => ({ ...f, site_id: e.target.value || null }))}
                className="mt-1 w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
              >
                <option value="">— Toda la cuenta (sin sitio específico) —</option>
                {sites.map((s) => (
                  <option key={s.id} value={s.id}>{s.name}</option>
                ))}
              </select>
            </div>

            {subError && (
              <p className="text-sm text-red-500 bg-red-500/10 rounded-lg px-3 py-2">{subError}</p>
            )}

            <div className="flex gap-2">
              <Button
                variant="outline"
                className="flex-1"
                onClick={() => { setShowSubModal(false); setSubError(null); }}
              >
                Cancelar
              </Button>
              <Button
                className="flex-1"
                disabled={!subForm.product_id || !subForm.next_billing_date || savingSub}
                onClick={handleSaveSub}
              >
                {savingSub
                  ? <Loader2 className="w-4 h-4 animate-spin mr-1" />
                  : <Plus className="w-4 h-4 mr-1" />}
                {editingSub ? 'Guardar cambios' : 'Agregar plan'}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* ── Delegation modal ── */}
      {delegateModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 px-4">
          <div className="bg-card border border-border rounded-2xl shadow-xl w-full max-w-md flex flex-col max-h-[90vh]">
            {/* Header */}
            <div className="flex items-center justify-between px-6 pt-5 pb-4 border-b border-border">
              <div>
                <h3 className="font-semibold text-foreground text-base">
                  Permisos de {delegateModal.profile?.full_name ?? 'miembro'}
                </h3>
                <p className="text-xs text-muted-foreground mt-0.5">{delegateModal.profile?.email}</p>
              </div>
              <button
                className="text-muted-foreground hover:text-foreground p-1 rounded"
                onClick={() => setDelegateModal(null)}
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Body */}
            <div className="overflow-y-auto flex-1 px-6 py-5 space-y-6">
              {/* Role */}
              <div className="space-y-2">
                <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Rol en la cuenta</p>
                <div className="grid grid-cols-3 gap-2">
                  {(Object.keys(MEMBER_ROLE_CONFIG) as AccountMemberRole[]).map((r) => {
                    const cfg = MEMBER_ROLE_CONFIG[r];
                    return (
                      <button
                        key={r}
                        type="button"
                        onClick={() => setDelegateRole(r)}
                        className={cn(
                          'flex items-center gap-1.5 px-2 py-2 rounded-lg text-xs font-medium border transition-colors',
                          delegateRole === r
                            ? 'border-primary bg-primary/10 text-primary'
                            : 'border-border text-muted-foreground hover:bg-muted',
                        )}
                      >
                        {cfg.icon}{cfg.label}
                      </button>
                    );
                  })}
                </div>
                {delegateRole === 'owner' && delegateModal?.role !== 'owner' && (
                  <p className="text-xs text-amber-600 dark:text-amber-400">
                    ⚠ Estás promoviendo a este miembro como propietario. La cuenta puede tener múltiples propietarios.
                  </p>
                )}
              </div>

              {/* Capabilities */}
              <div className="space-y-3">
                <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Funciones permitidas</p>
                {(
                  [
                    { key: 'can_view_cameras',  label: 'Ver cámaras' },
                    { key: 'can_view_panels',   label: 'Ver paneles de alarma' },
                    { key: 'can_arm_disarm',    label: 'Armar / Desarmar' },
                    { key: 'can_view_history',  label: 'Ver historial de eventos' },
                  ] as { key: keyof MemberCapabilities; label: string }[]
                ).map(({ key, label }) => (
                  <label key={key} className="flex items-center justify-between cursor-pointer">
                    <span className="text-sm text-foreground">{label}</span>
                    <input
                      type="checkbox"
                      className="w-4 h-4 accent-primary"
                      checked={delegateCaps[key]}
                      onChange={(e) =>
                        setDelegateCaps((prev) => ({ ...prev, [key]: e.target.checked }))
                      }
                    />
                  </label>
                ))}
              </div>

              {/* Site grants */}
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                    Acceso a sitios
                  </p>
                  <span className="text-xs text-muted-foreground">
                    {delegateSiteIds.length === 0
                      ? 'Acceso completo'
                      : `${delegateSiteIds.length} seleccionado(s)`}
                  </span>
                </div>
                {loadingGrants ? (
                  <div className="flex justify-center py-4">
                    <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
                  </div>
                ) : sites.length === 0 ? (
                  <p className="text-sm text-muted-foreground">Sin sitios en esta cuenta.</p>
                ) : (
                  <>
                    {/* All-sites toggle */}
                    <label className={`flex items-center gap-3 cursor-pointer px-3 py-2 rounded-lg border transition-colors ${
                      delegateSiteIds.length === 0
                        ? 'border-primary bg-primary/10'
                        : 'border-border bg-muted/30'
                    }`}>
                      <input
                        type="checkbox"
                        className="w-4 h-4 accent-primary shrink-0"
                        checked={delegateSiteIds.length === 0}
                        onChange={(e) => {
                          if (e.target.checked) setDelegateSiteIds([]);
                        }}
                      />
                      <span className={`text-sm font-medium ${delegateSiteIds.length === 0 ? 'text-primary' : 'text-foreground'}`}>
                        Todos los sitios
                      </span>
                      {delegateSiteIds.length === 0 && (
                        <span className="ml-auto text-xs text-primary font-medium">Activo</span>
                      )}
                    </label>
                    {/* Individual site checkboxes */}
                    <div className="space-y-1 max-h-40 overflow-y-auto pr-1">
                      {sites.map((s) => (
                        <label key={s.id} className="flex items-center gap-3 cursor-pointer">
                          <input
                            type="checkbox"
                            className="w-4 h-4 accent-primary shrink-0"
                            checked={delegateSiteIds.includes(s.id)}
                            onChange={(e) =>
                              setDelegateSiteIds((prev) =>
                                e.target.checked
                                  ? [...prev, s.id]
                                  : prev.filter((id) => id !== s.id),
                              )
                            }
                          />
                          <span className="text-sm text-foreground truncate">{s.name}</span>
                        </label>
                      ))}
                    </div>
                    <p className="text-xs text-muted-foreground">
                      Marca sitios específicos para restringir el acceso, o activa "Todos los sitios" para acceso completo.
                    </p>
                  </>
                )}
              </div>

              {/* Camera grants */}
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                    Acceso a cámaras
                  </p>
                  <span className="text-xs text-muted-foreground">
                    {delegateCameraIds.length === 0
                      ? 'Acceso completo'
                      : `${delegateCameraIds.length} seleccionada(s)`}
                  </span>
                </div>
                {loadingGrants ? (
                  <div className="flex justify-center py-4">
                    <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
                  </div>
                ) : cameras.length === 0 ? (
                  <p className="text-sm text-muted-foreground">Sin cámaras en esta cuenta.</p>
                ) : (
                  <>
                    {/* All-cameras toggle */}
                    <label className={`flex items-center gap-3 cursor-pointer px-3 py-2 rounded-lg border transition-colors ${
                      delegateCameraIds.length === 0
                        ? 'border-primary bg-primary/10'
                        : 'border-border bg-muted/30'
                    }`}>
                      <input
                        type="checkbox"
                        className="w-4 h-4 accent-primary shrink-0"
                        checked={delegateCameraIds.length === 0}
                        onChange={(e) => { if (e.target.checked) setDelegateCameraIds([]); }}
                      />
                      <span className={`text-sm font-medium ${delegateCameraIds.length === 0 ? 'text-primary' : 'text-foreground'}`}>
                        Todas las cámaras
                      </span>
                      {delegateCameraIds.length === 0 && (
                        <span className="ml-auto text-xs text-primary font-medium">Activo</span>
                      )}
                    </label>
                    {/* Individual camera checkboxes */}
                    <div className="space-y-1 max-h-40 overflow-y-auto pr-1">
                      {cameras.map((cam) => (
                        <label key={cam.id} className="flex items-center gap-3 cursor-pointer">
                          <input
                            type="checkbox"
                            className="w-4 h-4 accent-primary shrink-0"
                            checked={delegateCameraIds.includes(cam.id)}
                            onChange={(e) =>
                              setDelegateCameraIds((prev) =>
                                e.target.checked
                                  ? [...prev, cam.id]
                                  : prev.filter((id) => id !== cam.id),
                              )
                            }
                          />
                          <span className="text-sm text-foreground truncate">{cam.name}</span>
                        </label>
                      ))}
                    </div>
                    <p className="text-xs text-muted-foreground">
                      Marca cámaras específicas para restringir el acceso, o activa "Todas las cámaras" para acceso completo.
                    </p>
                  </>
                )}
              </div>

              {/* All client_sites — cross-account view */}
              <div className="space-y-3">
                <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                  Todos los accesos asignados
                </p>
                {loadingGrants ? (
                  <div className="flex justify-center py-2">
                    <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" />
                  </div>
                ) : allClientSites.length === 0 ? (
                  <p className="text-sm text-muted-foreground">Sin accesos asignados.</p>
                ) : (
                  <div className="space-y-1.5 max-h-48 overflow-y-auto pr-1">
                    {allClientSites.map((s) => (
                      <div key={s.site_id} className="flex items-center justify-between gap-2 rounded-lg border border-border px-3 py-1.5">
                        <div className="min-w-0">
                          <p className="text-sm text-foreground truncate">{s.site_name}</p>
                          <p className="text-xs text-muted-foreground truncate">{s.account_name ?? s.account_id ?? '—'}</p>
                        </div>
                        <button
                          className="shrink-0 text-muted-foreground hover:text-destructive transition-colors p-0.5 rounded disabled:opacity-40"
                          disabled={removingSiteId === s.site_id}
                          onClick={() => delegateModal && handleRemoveClientSite(delegateModal.user_id, s.site_id)}
                        >
                          {removingSiteId === s.site_id
                            ? <Loader2 className="w-4 h-4 animate-spin" />
                            : <X className="w-4 h-4" />}
                        </button>
                      </div>
                    ))}
                  </div>
                )}
                <p className="text-xs text-muted-foreground">
                  Quita accesos que ya no correspondan a este usuario.
                </p>
              </div>
            </div>

            {/* Footer */}
            <div className="flex justify-end gap-3 px-6 py-4 border-t border-border">
              <Button variant="outline" size="sm" onClick={() => setDelegateModal(null)}>
                Cancelar
              </Button>
              <Button size="sm" onClick={handleSaveDelegate} disabled={savingGrants}>
                {savingGrants
                  ? <><Loader2 className="w-4 h-4 animate-spin mr-2" />Guardando…</>
                  : 'Guardar'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Row({ label, value }: { label: string; value: string | null | undefined }) {
  if (!value) return null;
  return (
    <div className="flex items-start gap-2">
      <span className="text-xs text-muted-foreground w-36 shrink-0 pt-0.5">{label}</span>
      <span className="text-sm text-foreground">{value}</span>
    </div>
  );
}

// InvoiceFelActions is now in @/components/billing/InvoiceFelActions
